import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { LorenFormField } from "../../../loren-entity-form-card/interfaces/loren-form-field.interface";
import { LorenFieldTypes } from "../../../loren-entity-form-card/enums/loren-field-types.enum";
import { LorenPeriod } from "../../../loren-period-selector/interfaces/loren-period.interface";
import { FormFieldInteraction } from "../../../loren-entity-form-card/components/loren-entity-form-card/interfaces/form-field-interaction.interface";
import { LorenPeriodSelectorService } from "../../../loren-period-selector/services/loren-period-selector-service/loren-period-selector.service";
import { LorenDateUtilsService } from "../../../../services/loren-date-utils-service/loren-date-utils.service";
import { Instant, LocalTime, ZoneId } from "@js-joda/core";

@Component({
    selector: "loren-period-selector-form",
    templateUrl: "./loren-period-selector-form.component.html",
    styleUrls: ["./loren-period-selector-form.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LorenPeriodSelectorFormComponent implements OnInit {
  @Input() isUsedInDialog: boolean;
  @Output() periodChange = new EventEmitter<LorenPeriod>();

  from: LorenFormField;
  to: LorenFormField;
  lastSelectedPeriod: LorenPeriod;
  now = LorenDateUtilsService.getMomentNow().startOf("date");
  lastSelectedTimeFrom = "00:00";
  lastSelectedTimeTo = this.getNowHoursOnly();
  showFromTo: boolean;
  timePickerEnabled = true;

  ngOnInit(): void {
    this.from = {
      name: "fromDate",
      hidden: true,
      value: LorenDateUtilsService.getSubtractedFromNowDate(1).toISOString(),
      label: "operations.from_date",
      formControlType: LorenFieldTypes.DATETIME,
    };

    this.to = {
      name: "toDate",
      hidden: true,
      value: LorenDateUtilsService.getMomentNow().toISOString(),
      label: "operations.to_date",
      formControlType: LorenFieldTypes.DATETIME,
    };
  }

  handleCustomPeriodSelection($event: FormFieldInteraction) {
    const customPeriod = LorenPeriodSelectorService.getPeriods().find(
      (x) => x.isCustomPeriod
    );
    const zone = LorenDateUtilsService.getMomentTz($event.fieldCurrentValue);

    customPeriod.fromDate = null;
    customPeriod.toDate = null;

    if ($event.fieldName === this.from.name) {
      this.from.value = zone.format();
    }

    if ($event.fieldName === this.to.name) {
      this.to.value = zone.format();
    }

    this.changeTimeToDefaultValue();
    this.prepareSelectedPeriod();
  }

  handlePeriodChange($event: LorenPeriod) {
    this.lastSelectedPeriod = $event;
    this.prepareComponentProperties();

    if (!this.lastSelectedPeriod.isCustomPeriod) {
      this.periodChange.emit($event);
    } else {
      this.prepareSelectedPeriod();
    }
  }

  prepareSelectedPeriod() {
    const diff = LorenDateUtilsService.getDaysDiff(
      LorenDateUtilsService.getMoment(this.to.value).startOf("date"),
      LorenDateUtilsService.getMoment(this.from.value).startOf("date")
    );

    if (this.from.value && this.to.value && diff >= 0) {
      let fromDate = new Date(this.from.value).toISOString();
      let toDate = new Date(this.to.value).toISOString();

      if (this.lastSelectedTimeFrom && this.lastSelectedTimeTo) {
        fromDate = this.getUTCDateString(fromDate, this.lastSelectedTimeFrom);
        toDate = this.getUTCDateString(toDate, this.lastSelectedTimeTo);
      }

      this.periodChange.emit({
        fromDate,
        toDate,
        isCustomPeriod: true,
        days: diff,
        label: "period.custom_period",
      });
    }
  }

  private prepareComponentProperties() {
    this.showFromTo =
      this.lastSelectedPeriod && this.lastSelectedPeriod.isCustomPeriod;
  }

  private changeTimeToDefaultValue() {
    const toMoment = LorenDateUtilsService.getMoment(this.to.value).startOf(
      "date"
    );
    const diff = LorenDateUtilsService.getDaysDiff(this.now, toMoment);
    if (diff !== 0) {
      this.lastSelectedTimeTo = "23:59";
    } else {
      this.lastSelectedTimeTo = this.getNowHoursOnly();
    }

    // TODO Hack for modifying default time dynamically based on client requirements
    // TODO consider using another change detection approach in future
    this.timePickerEnabled = false;
    setTimeout(() => (this.timePickerEnabled = true), 1);
  }

  private getNowHoursOnly() {
    return LorenDateUtilsService.getMomentNow().format('HH:mm');
  }

  private getUTCDateString(date: string, time: string) {
    // Date comes in as UTC, time comes as local time - no idea why but this will need to be reworked in a sane way
    return Instant.parse(date) // Parse UTC instant
      .atZone(ZoneId.UTC) // convert to zoned DT and get the date only
      .toLocalDate()
      .atTime(LocalTime.parse(time)) // Set the time
      .atZone(ZoneId.systemDefault()) // Assign correct timezone
      .toInstant() // Get well-formatted ISO8601 instant :)
      .toString();
  }
}
